import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, ChevronDown, Loader2, Map as MapIcon } from 'lucide-react';
import { DocumentTitle } from '../components/DocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import GymCard from '../components/GymCard';
import GymCardSkeleton from '../src/components/directory/GymCardSkeleton';
import GoogleMapView, { MapGym } from '../components/GoogleMapView';
import { Button } from '../src/components/ui/button';
import { Banner } from '../src/components/ui/banner';
import { EmptyState } from '../src/components/ui/empty-state';
import { PageTitle } from '../src/components/ui/typography';
import SignedOutPrompt from '../components/SignedOutPrompt';
import { Gym, ViewMode, FavouriteRefDoc } from '../types';

import { getFavourites, removeFavourite } from '../services/favouritesService';
import { batchGetGymsFromDisplay } from '../services/locationService';
import { buildGymUrl } from '../lib/slugs';
import { DEFAULT_STATE, stateDisplayToSlug, stateSlugToDisplay, LAUNCHED_STATES } from '../lib/stateCore.mjs';
import type { StateSlug } from '../lib/stateCore.mjs';
import { matchesBreakpointUp } from '../lib/breakpoints';

type SortOption = 'recent' | 'rating' | 'reviews';

const FAVOURITES_PER_PAGE = 12;

const FavouritesPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, isLoading: authLoading, openAuthModal } = useAuth();

    // Data state
    const [favouriteRefs, setFavouriteRefs] = useState<FavouriteRefDoc[]>([]);
    const [favouriteGyms, setFavouriteGyms] = useState<Map<string, Gym>>(new Map());

    // UI state
    const [isLoadingRefs, setIsLoadingRefs] = useState(true);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<SortOption>('recent');
    const [visibleCount, setVisibleCount] = useState(FAVOURITES_PER_PAGE);
    /** The tab the visitor EXPLICITLY chose. null = they have not chosen; the default
     *  is derived during render by `effectiveState` below, never assigned here. */
    const [activeState, setActiveState] = useState<StateSlug | null>(null);
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
    const [removingId, setRemovingId] = useState<string | null>(null);

    // Layout state — safe defaults that match SSG output; real values set in useEffect.
    // isMobile (<768) drives genuine touch behaviours (marker-scroll timing, GoogleMapView).
    // isNarrowViewport (<lg 1024) drives the map/list LAYOUT: below lg → full-screen map/list
    // toggle, at lg+ → split. Kept in sync with DirectoryPage so both pages behave identically.
    const [isMobile, setIsMobile] = useState(false);
    const [isNarrowViewport, setIsNarrowViewport] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.LIST);
    const [highlightedGymId, setHighlightedGymId] = useState<string | null>(null);
    const [hoveredGymId, setHoveredGymId] = useState<string | null>(null);
    const cardRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});
    const sortDropdownRef = useRef<HTMLDivElement>(null);


    // Tab title is set by <DocumentTitle> in this component's JSX.

    // Track viewport — sets both the touch flag (<768) and the map-layout flag (<1024).
    useEffect(() => {
        const handleResize = () => {
            // Item 1: rem-based, matching the stylesheet's own `md:`/`lg:` tiers
            // rather than counting pixels that disagree at a raised root font size.
            setIsMobile(!matchesBreakpointUp('md'));
            setIsNarrowViewport(!matchesBreakpointUp('lg'));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset the view when crossing the narrow/wide boundary so we never land in an
    // unsupported state: narrow can't show SPLIT (drop to LIST); widening out of a
    // full-screen MAP restores the list beside it (SPLIT). Mirrors DirectoryPage.
    const lastIsNarrowRef = useRef(isNarrowViewport);
    useEffect(() => {
        if (isNarrowViewport !== lastIsNarrowRef.current) {
            lastIsNarrowRef.current = isNarrowViewport;
            setViewMode((prev) =>
                isNarrowViewport
                    ? (prev === ViewMode.SPLIT ? ViewMode.LIST : prev)
                    : (prev === ViewMode.MAP ? ViewMode.SPLIT : prev)
            );
        }
    }, [isNarrowViewport]);

    // Redirect to auth if not logged in
    useEffect(() => {
        if (!authLoading && !user) {
            openAuthModal({
                type: 'favourite',
                gymId: '',
                returnUrl: '/favourites'
            });
        }
    }, [authLoading, user, openAuthModal]);

    // Handle outside click for sort dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
                setIsSortDropdownOpen(false);
            }
        };

        if (isSortDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSortDropdownOpen]);


    /**
     * Load gym details for all favourites
     */
    const loadDetails = async (refs: FavouriteRefDoc[]) => {
        if (refs.length === 0) {
            setIsLoadingDetails(false);
            return;
        }

        setIsLoadingDetails(true);
        setLoadingError(null);

        try {
            const placeIds = refs.map(r => r.placeId);

            // Display docs already carry override-applied (effective) tags —
            // sync-display bakes them in — so no separate enrichment read is
            // needed here (item 13). One collection instead of two.
            const gyms = await batchGetGymsFromDisplay(placeIds);

            const mappedGyms = gyms.map(g => {
                const data = g as any;
                return {
                    ...g,
                    // ⚠️ L13 (2026-08-20) — THIS ORDER IS CORRECT HERE AND IS NOT THE ONE THAT
                    // WAS FLIPPED. `gyms` has ALREADY been mapped by
                    // `batchGetGymsFromDisplay` (services/locationService.ts), so `data`
                    // is a Gym whose `allStyles` is populated — not a raw display document.
                    // The two mapping sites INSIDE locationService read the raw document and
                    // were flipped, because production stores `styles`/`experienceLevel` and
                    // has `allStyles`/`experienceLevels` on ZERO of 2,750 docs. Here the
                    // left-hand operand is the live one, so the order already reads true.
                    // Recorded because this is the THIRD site reading these names and the
                    // original finding said there were two.
                    allStyles: data.allStyles || data.styles || [],
                    experienceLevels: data.experienceLevels || data.experienceLevel || [],
                    google_rating: data.google_rating ?? data.rating ?? null,
                    google_user_ratings_total: data.google_user_ratings_total ?? data.userRatingCount ?? null
                } as Gym;
            });

            const newGymsMap = new Map<string, Gym>();
            mappedGyms.forEach(g => newGymsMap.set(g.id, g));
            setFavouriteGyms(newGymsMap);
        } catch (error) {
            console.error('[FavouritesPage] Loading details failed:', error);
            setLoadingError('Failed to load gym details. Please try again.');
        } finally {
            setIsLoadingDetails(false);
        }
    };

    /**
     * Load favourite refs from Firestore
     */
    const loadFavourites = async () => {
        if (!user) return;

        setIsLoadingRefs(true);
        try {
            const userFavourites = await getFavourites(user.uid);
            setFavouriteRefs(userFavourites);
            // Kick off loading details immediately after refs load
            await loadDetails(userFavourites);
        } catch (error) {
            console.error('[FavouritesPage] Error loading favourites:', error);
        } finally {
            setIsLoadingRefs(false);
        }
    };

    useEffect(() => {
        if (user) {
            loadFavourites();
        }
    }, [user]);

    const handleRemoveFavourite = async (gymId: string) => {
        if (!user) return;

        setRemovingId(gymId);
        try {
            await removeFavourite(user.uid, gymId);
            setFavouriteRefs(prev => prev.filter(f => f.placeId !== gymId));
        } catch (error) {
            console.error('[FavouritesPage] Error removing favourite:', error);
        } finally {
            setRemovingId(null);
        }
    };

    const handleMarkerClick = (gym: Gym) => {
        setHighlightedGymId(gym.id);
        if (isNarrowViewport) setViewMode(ViewMode.LIST);

        // Standardized scroll behavior
        requestAnimationFrame(() => {
            setTimeout(() => {
                const cardEl = cardRefs.current[gym.id];
                if (cardEl) {
                    cardEl.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });
                }
            }, isNarrowViewport ? 150 : 50);
        });
    };

    /**
     * Join refs + display gyms into unified objects for display
     */
    const favouriteItems = useMemo(() => {
        return favouriteRefs.map(ref => {
            // Display docs already carry effective (override-applied) tags, so we
            // use them directly — no enrichment merge needed (item 13).
            const gym = favouriteGyms.get(ref.placeId) || null;
            return { ref, gym };
        });
    }, [favouriteRefs, favouriteGyms]);

    /**
     * Final sorted items for rendering
     */
    const sortedItems = useMemo(() => {
        const copy = [...favouriteItems];

        if (sortBy === 'recent') {
            return copy.sort((a, b) => {
                const aTime = a.ref.savedAt?.toDate?.()?.getTime?.() || 0;
                const bTime = b.ref.savedAt?.toDate?.()?.getTime?.() || 0;
                return bTime - aTime;
            });
        }

        if (sortBy === 'rating') {
            return copy.sort((a, b) => {
                if (!a.gym || a.gym.google_rating == null) return 1;
                if (!b.gym || b.gym.google_rating == null) return -1;

                if (b.gym.google_rating !== a.gym.google_rating) {
                    return b.gym.google_rating - a.gym.google_rating;
                }
                const bReviews = b.gym.google_user_ratings_total || 0;
                const aReviews = a.gym.google_user_ratings_total || 0;
                if (bReviews !== aReviews) return bReviews - aReviews;
                return a.gym.name.localeCompare(b.gym.name);
            });
        }

        if (sortBy === 'reviews') {
            return copy.sort((a, b) => {
                if (!a.gym || a.gym.google_user_ratings_total == null) return 1;
                if (!b.gym || b.gym.google_user_ratings_total == null) return -1;

                if (b.gym.google_user_ratings_total !== a.gym.google_user_ratings_total) {
                    return b.gym.google_user_ratings_total - a.gym.google_user_ratings_total;
                }
                const bRating = b.gym.google_rating || 0;
                const aRating = a.gym.google_rating || 0;
                if (bRating !== aRating) return bRating - aRating;
                return a.gym.name.localeCompare(b.gym.name);
            });
        }

        return copy;
    }, [favouriteItems, sortBy]);

    /**
     * ⭐ SAVED GYMS SPLIT BY STATE (operator-ruled 2026-08-16 — this lane proposed the
     * mechanism; the operator chose it over always-visible tabs and a filter dropdown).
     *
     * Which states the visitor actually has favourites in, in LAUNCHED_STATES order so the
     * tabs read NSW then VIC rather than in save order. Derived — a third state needs no
     * edit here.
     */
    const statesWithFavourites = useMemo(() => {
        const present = new Set(
            favouriteItems
                .map((i) => (i.gym ? stateDisplayToSlug(i.gym.state) : null))
                .filter((s): s is StateSlug => s !== null),
        );
        return (LAUNCHED_STATES as readonly StateSlug[]).filter((s) => present.has(s));
    }, [favouriteItems]);

    /**
     * ⭐ THE STATE ACTUALLY IN VIEW — derived during RENDER, not assigned by an effect.
     *
     * ⚠️ THIS WAS AN EFFECT AND THE CHANGE IS A BUG FIX, caught by a test rather than by
     * eye. With an effect, the first render after the favourites load still had
     * `activeState === null`, so the page painted EVERY state's gyms for one frame and then
     * snapped to one — a visible flash, and on a slow device a real "why did half my saved
     * gyms disappear?" moment. Deriving it during render means the very first paint is
     * already correct and there is no intermediate state to see.
     *
     * `activeState` now means ONLY "the tab the visitor explicitly chose"; null means they
     * have not chosen, and the default below applies. That separation is what lets the
     * default follow the data without ever overriding a choice.
     */
    const effectiveState = useMemo<StateSlug | null>(() => {
        if (statesWithFavourites.length < 2) return null;
        if (activeState !== null && statesWithFavourites.includes(activeState)) return activeState;
        const counts = new Map<StateSlug, number>();
        for (const i of favouriteItems) {
            if (!i.gym) continue;
            const sl = stateDisplayToSlug(i.gym.state);
            counts.set(sl, (counts.get(sl) ?? 0) + 1);
        }
        return [...statesWithFavourites].sort(
            (a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0),
        )[0] ?? null;
    }, [statesWithFavourites, activeState, favouriteItems]);

    /**
     * ⚠️ ITEMS WHOSE GYM HAS NOT LOADED CANNOT BE ATTRIBUTED TO A STATE, and this is the
     * decision that matters most in this block. `FavouriteRefDoc` carries only a placeId
     * and a timestamp — no state — so a saved gym whose document is still loading, or has
     * been delisted, has nothing to filter on.
     *
     * They are shown in EVERY state view rather than filtered out. The two failure modes
     * are not symmetric: filtering them out makes a gym the visitor saved SILENTLY VANISH
     * from their list, which reads as data loss; showing them in both tabs is merely
     * redundant. Chosen deliberately, not by omission.
     */
    const visibleItems = useMemo(() => {
        if (effectiveState === null) return sortedItems;
        return sortedItems.filter(
            (i) => !i.gym || stateDisplayToSlug(i.gym.state) === effectiveState,
        );
    }, [sortedItems, effectiveState]);


    // Pagination — mirrors useDirectoryResults' client-side Load-More idiom
    // (FAVOURITES_PER_PAGE / visibleCount / slice / handleLoadMore). Reset to the
    // first page whenever the sort changes so a re-sort never strands the user in a
    // truncated tail. NOTE: useDirectoryResults resets on its SOURCE list ([allGyms]),
    // not on sortBy (its re-sort runs in a worker); a client-side re-sortable list
    // warrants the sort-reset, so this is an intentional, correct divergence.
    // ⭐ `activeState` joins it for the same reason: switching states must not strand the
    // visitor deep in the previous state's tail. The existing pagination guard pins this
    // convention for sort; the state tab follows it rather than inventing a second one.
    useEffect(() => {
        setVisibleCount(FAVOURITES_PER_PAGE);
    }, [sortBy, effectiveState]);

    const handleLoadMore = () => setVisibleCount(prev => prev + FAVOURITES_PER_PAGE);

    // Map markers from gym items
    const gymsForMap = useMemo(() => {
        // ⚠️ visibleItems, NOT sortedItems: the map must follow the state tab. Reading the
        // unfiltered list here would plot BOTH states' markers beside a list showing one —
        // a discrepancy that looks correct in the list and is wrong on the map, which is
        // the half a reviewer is least likely to check.
        return visibleItems
            .filter(item => item.gym && item.gym.lat && item.gym.lng)
            .map(item => item.gym!);
    }, [visibleItems]);

    const sortLabels: Record<SortOption, string> = {
        recent: 'Recently saved',
        rating: 'Rating',
        reviews: 'Review count'
    };

    const isListView = viewMode === ViewMode.LIST;
    const isMapView = viewMode === ViewMode.MAP;

    // Lazy-load Maps on first need, then latch (same pattern as DirectoryPage):
    // the page starts in LIST view, so don't pay the Maps API cost until the
    // user first shows the map. Latching keeps the instance alive on Hide map,
    // preserving camera position and markers for the next Show map.
    const wantsMap = !isListView;
    const hasWantedMapRef = useRef(false);
    if (wantsMap) hasWantedMapRef.current = true;
    const shouldLoadMaps = wantsMap || hasWantedMapRef.current;

    if (authLoading) {
        return (
            <div className="min-h-screen bg-surface-canvas flex items-center justify-center">
                <Loader2 className="h-16 w-16 text-brand-primary animate-spin" />
            </div>
        );
    }

    // Not signed in (item 4, 2026-08-14). This screen's WORDING is unchanged — the
    // operator ruled it stays word-for-word — but until ProtectedRoute stopped
    // redirecting to '/' it was unreachable: no visitor could ever see it. The
    // wrapper stays here rather than moving into SignedOutPrompt because this page's
    // layout is a fixed full-bleed pane below the header, unlike the other two.
    if (!user) {
        return (
            <>
                {/* ⭐ UI-112 — see AccountPage's signed-out branch for the full note. The skip
                    link targeted `#main-content`, which did not exist here; measured live at
                    origin, /blog passing as the control.
                    ⚠️ THIS PAGE'S WRAPPER IS THE ONE THAT DIFFERS, and it stays that way: a
                    fixed full-bleed pane below the header, matching the signed-IN branch's own
                    <main> below rather than the other two screens' centred box. The operator
                    ruled this page unchanged word-for-word on 2026-08-14 — only the landmark
                    and the title are added. */}
                <DocumentTitle title="Favourites – Martial Arts Network" />
                <main id="main-content" className="fixed inset-x-0 bottom-0 top-header bg-surface-canvas flex flex-col">
                    <div className="flex-grow flex items-center justify-center px-4">
                        <SignedOutPrompt
                            icon={Heart}
                            heading="Sign in to view favourites"
                            body="Create an account to save your favourite gyms."
                        />
                    </div>
                </main>
            </>
        );
    }

    return (
        // <main> landmark (FAV1): other pages expose a main landmark; this page was
        // divs only. UI-46: offset below the header at EVERY breakpoint (`top-header`,
        // matching DirectoryPage) so the Sort + map/list controls are never blocked
        // by it. The former `lg:top-0` belonged to the removed desktop auto-hide.
        <main id="main-content" className="fixed inset-x-0 bottom-0 top-header flex flex-col bg-surface-canvas overflow-hidden">
            <DocumentTitle title="Favourites – Martial Arts Network" />
            <div className="flex-1 flex overflow-hidden relative bg-surface-canvas">
                {/* List content — collapses in map view (full-screen map); half-width in split. */}
                {/* ⚠️⚠️ THE MAP-VIEW BRANCH IS AN OVERLAY, NOT A COLLAPSE, AND THAT IS LOAD-BEARING.
                    This column used to become `w-0 opacity-0 invisible` in map view. With the header
                    now INSIDE it (item 4, full parity), collapsing would take the header with it —
                    stranding a phone visitor on a full-screen map with no "Show list" control and no
                    way back. Mounting it as a transparent, click-through overlay is exactly how
                    DirectoryPage has always done it, and it is what makes moving the header safe.
                    ⚠️ `pointer-events-none` here is why the map stays draggable underneath; the header
                    re-enables pointer events on itself. Removing either half breaks the other. */}
                <div className={`
                    flex flex-col bg-surface-canvas overflow-hidden
                    ${isMapView ? 'absolute inset-0 z-10 bg-transparent pointer-events-none' : isListView ? 'w-full opacity-100' : 'w-full md:w-1/2 opacity-100'}
                `}>
                    {/* Header — title, Sort and the map/list toggle.
                        ⭐ MOVED INSIDE THE LIST COLUMN 2026-08-17 (operator list item 4, FULL PARITY
                        ruled with the phone risk stated). It previously sat ABOVE the split container,
                        spanning the whole page — and that ONE structural difference caused BOTH
                        symptoms the operator reported: the controls landed at the far page-right
                        instead of beside the title, and the full-width row pushed the map down so it
                        started ~100px lower and was that much shorter than the directories'.
                        ⚠️ IN MAP VIEW IT NOW FLOATS OVER THE MAP because the COLUMN is absolute, not
                        because the header is — see the column's own note. `sticky` + `pointer-events-auto`
                        + a transparent background mirror DirectoryPage exactly.
                        ⚠️ `items-start` is KEPT and is NOT a drift from the directories' `items-center`:
                        the operator ruled it kept (2026-08-17) because it pins the map toggle to the
                        same spot whether or not the title is showing, which the directories do not do. */}
                    <div className={`px-4 py-4 flex justify-between items-start flex-shrink-0 sticky top-0 z-40 pointer-events-auto ${isMapView ? 'bg-transparent' : 'bg-surface-canvas'}`}>
                        {/* Title hidden in map view; items-start on the bar pins the toggle to the
                            same top position (py-4 below the bar top) in BOTH views regardless of
                            whether the title is present — so the toggle is pixel-identical. */}
                        {!isMapView && (
                        <div className="flex flex-col">
                            <PageTitle className="text-2xl leading-tight text-brand-primary">
                                Favourites
                            </PageTitle>
                            <p className="text-sm text-text-muted mt-0.5">
                                Your saved martial arts gyms
                            </p>
                        </div>
                        )}

                        <div className="flex items-center gap-2 pointer-events-auto ml-auto">
                            {!isMapView && favouriteRefs.length > 0 && (
                                <div className="relative" ref={sortDropdownRef}>
                                    <Button
                                        variant="outline"
                                        size="compact"
                                        aria-label="Sort favourites"
                                        aria-haspopup="listbox"
                                        aria-expanded={isSortDropdownOpen}
                                        aria-controls="favourites-sort-options"
                                        onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                                        className="flex justify-between"
                                    >
                                        <span className="hidden lg:inline">{sortLabels[sortBy]}</span>
                                        <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${isSortDropdownOpen ? 'rotate-180' : ''}`} />
                                    </Button>
                                    {isSortDropdownOpen && (
                                        <div role="listbox" id="favourites-sort-options" className="absolute top-full right-0 mt-1 w-40 bg-white border border-border-default rounded-lg z-50 overflow-hidden shadow-lg">
                                            {(['recent', 'rating', 'reviews'] as SortOption[]).map((option) => (
                                                <button
                                                    key={option}
                                                    role="option"
                                                    aria-selected={sortBy === option}
                                                    onClick={() => {
                                                        setSortBy(option);
                                                        setIsSortDropdownOpen(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sortBy === option
                                                        ? 'bg-brand-primary/10 text-brand-primary font-semibold'
                                                        : 'text-text-primary hover:bg-surface-canvas'
                                                        }`}
                                                >
                                                    {sortLabels[option]}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {favouriteRefs.length > 0 && (
                                <Button
                                    variant="outline"
                                    aria-pressed={viewMode !== ViewMode.LIST}
                                    aria-label={isNarrowViewport ? (viewMode === ViewMode.MAP ? 'Show list' : 'Show map') : (viewMode === ViewMode.SPLIT ? 'Hide map' : 'Show map')}
                                    onClick={() => {
                                        if (isNarrowViewport) {
                                            setViewMode(viewMode === ViewMode.LIST ? ViewMode.MAP : ViewMode.LIST);
                                        } else {
                                            setViewMode(viewMode === ViewMode.SPLIT ? ViewMode.LIST : ViewMode.SPLIT);
                                        }
                                    }}
                                    size="compact"
                                    pressed={isNarrowViewport && viewMode === ViewMode.MAP}
                                    className="flex justify-between"
                                    title={isNarrowViewport ? (viewMode === ViewMode.MAP ? 'Show list' : 'Show map') : (viewMode === ViewMode.SPLIT ? 'Hide map' : 'Show map')}
                                >
                                    <MapIcon size={16} className="flex-shrink-0" />
                                    <span className="hidden lg:inline">
                                        {!isNarrowViewport && (viewMode === ViewMode.SPLIT ? 'Hide map' : 'Show map')}
                                    </span>
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={`
                        flex-1 bg-surface-canvas overflow-y-auto
                        ${isListView ? 'px-8 py-6' : 'p-4'}
                    `}>
                        {loadingError && (
                            // max-w-[1120px] is intentionally scoped to the list-view inner content
                            // (banner + grid below). The page itself is full-bleed (fixed inset-0)
                            // to match DirectoryPage's map-driven pattern. This cap exists to keep
                            // cards from stretching across a 27"+ monitor when only a handful of
                            // favourites are pinned. NOT page-level container drift.
                            <Banner
                                variant="danger"
                                className="max-w-[1120px] mx-auto mb-6"
                                action={<Button variant="ghost" size="sm" onClick={() => loadDetails(favouriteRefs)}>Retry</Button>}
                            >
                                {loadingError}
                            </Banner>
                        )}

                        {/* ⭐ STATE TABS (operator-ruled 2026-08-16). Rendered ONLY when the visitor
                            has favourites in more than one state — a single-state visitor sees the
                            page exactly as before, with no tab, no filter and no empty second tab
                            to click into. That conditional is the ruling, not an optimisation.

                            Tabs, not links: this filters a client-side list and mints no URL, so
                            `aria-selected` on real buttons inside a `tablist` is the honest role.
                            The pill look matches the site's existing state pills deliberately, so
                            the control reads as native rather than as a new idiom.

                            ⚠️ The count in each label is the STATE's total, not the visible page —
                            it must not move when Load More is pressed, or it stops meaning
                            "how many I saved here". */}
                        {statesWithFavourites.length > 1 && (
                            <div
                                role="tablist"
                                aria-label="Filter saved gyms by state"
                                className="flex w-full gap-3 mb-6 max-w-[1120px] mx-auto"
                            >
                                {statesWithFavourites.map((slug) => {
                                    const isActive = slug === effectiveState;
                                    const count = favouriteItems.filter(
                                        (i) => i.gym && stateDisplayToSlug(i.gym.state) === slug,
                                    ).length;
                                    return (
                                        <button
                                            key={slug}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveState(slug)}
                                            className={`inline-flex flex-1 items-center justify-center min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${
                                                isActive
                                                    ? 'bg-brand-primary text-white border-transparent'
                                                    : 'bg-surface-base text-text-primary border-border-default hover:border-text-subtle'
                                            }`}
                                        >
                                            {stateSlugToDisplay(slug)} ({count})
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* List-view-only cap: 3-col grid centered with max-w-[1120px] keeps cards
                            from stretching across very wide monitors. SPLIT view (when the map is
                            visible) drops the cap because the panel itself is only 50% of viewport.
                            Discrepancy with DirectoryPage (no cap in either view) is a deliberate
                            difference — fewer favourites typically means stretched cards look sparse. */}
                        <div
                            aria-busy={isLoadingRefs || isLoadingDetails}
                            className={`
                            grid gap-5 auto-rows-max
                            ${isListView
                                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-[1120px] mx-auto'
                                : 'grid-cols-1 lg:grid-cols-2'}
                        `}>
                            {isLoadingRefs || isLoadingDetails ? (
                                // Skeleton grid mirrors the loaded GymCard layout (policy: content-rich
                                // card grid → skeleton, not spinner). sr-only status announces the load.
                                <>
                                    <span className="sr-only" role="status" aria-live="polite">Loading favourites</span>
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <GymCardSkeleton key={i} />
                                    ))}
                                </>
                            ) : favouriteRefs.length === 0 ? (
                                <EmptyState
                                    headingLevel={2}
                                    className="col-span-full"
                                    title="No Favourites Yet"
                                    description="Browse gyms and tap the heart icon to save them here."
                                    action={
                                        <Button variant="primary" asChild>
                                            <Link to="/gyms" state={{ triggerFilters: true }}>
                                                Browse Gyms
                                            </Link>
                                        </Button>
                                    }
                                />
                            ) : (
                                visibleItems.slice(0, visibleCount).map((item) => (
                                    <div
                                        key={item.ref.placeId}
                                        ref={(el) => { cardRefs.current[item.ref.placeId] = el; }}
                                        className="w-full"
                                    >
                                        {item.gym ? (
                                            <GymCard
                                                gym={item.gym}
                                                // A user's favourites are not confined to one state, so both the
                                                // anchor and the click target are addressed from each gym's own state.
                                                href={buildGymUrl(
                                                    item.gym.name, item.gym.stableId || item.gym.id, item.gym.suburb, item.gym.postcode,
                                                    item.gym.state ? stateDisplayToSlug(item.gym.state) : DEFAULT_STATE,
                                                )}
                                                onClick={() => {
                                                    const gymUrl = buildGymUrl(
                                                        item.gym!.name, item.gym!.stableId || item.gym!.id, item.gym!.suburb, item.gym!.postcode,
                                                        item.gym!.state ? stateDisplayToSlug(item.gym!.state) : DEFAULT_STATE,
                                                    );
                                                    navigate(gymUrl, {
                                                        state: {
                                                            gym: item.gym,
                                                            returnUrl: '/favourites'
                                                        }
                                                    });
                                                }}
                                                isHighlighted={highlightedGymId === item.ref.placeId}
                                                onMouseEnter={() => setHoveredGymId(item.ref.placeId)}
                                                onMouseLeave={() => {
                                                    setHoveredGymId(null);
                                                    if (highlightedGymId === item.ref.placeId) setHighlightedGymId(null);
                                                }}
                                                isFavourite={true}
                                                onFavouriteToggle={() => handleRemoveFavourite(item.ref.placeId)}
                                                isFavouriteLoading={removingId === item.ref.placeId}
                                            />
                                        ) : (
                                            /* Fallback for failed details loading */
                                            <div className="w-full p-4 bg-white border border-border-default rounded-xl flex items-center justify-between">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-text-primary">Gym unavailable</span>
                                                    <span className="text-xs text-text-muted">{item.ref.placeId}</span>
                                                </div>
                                                <button
                                                    aria-label="Remove from favourites"
                                                    onClick={() => handleRemoveFavourite(item.ref.placeId)}
                                                    disabled={removingId === item.ref.placeId}
                                                    className="p-2 text-text-subtle hover:text-state-danger transition-colors"
                                                >
                                                    {removingId === item.ref.placeId ? (
                                                        <Loader2 size={18} className="animate-spin" />
                                                    ) : (
                                                        <Heart size={18} className="fill-state-danger text-state-danger" />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                            {visibleCount < visibleItems.length && (
                                <div className="col-span-full flex justify-center py-4">
                                    <Button variant="primary" onClick={handleLoadMore} className="px-6">
                                        Load More ({visibleItems.length - visibleCount} remaining)
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ⭐ MAP FRAMING MATCHED TO THE TWO DIRECTORY PAGES (operator-ruled 2026-08-17,
                    list item 4). The two surfaces differed in exactly ONE condition and it was
                    this one: the directories go full-bleed only on PHONE/TABLET in map view
                    (`isNarrowViewport && …MAP`), while this page went full-bleed whenever the
                    map was shown AT ANY WIDTH. On desktop that dropped the inset card — no
                    padding, no rounded corners, no border — so the map read as taller and
                    edge-to-edge here and framed there, on pages a visitor moves between.

                    ⚠️ `isMapView` ALONE IS THE BUG, not a shorthand for it. At ≥lg this page's
                    toggle yields SPLIT, so the desktop branch is reached through a different
                    view mode than the narrow one — which is exactly why keying on the mode
                    without the width silently changed desktop framing.
                    ⚠️ The STATE TABS above are untouched; they are this page's own feature
                    (UI-88) and the directories have no equivalent to copy. */}
                <div className={`
                    relative z-0 bg-surface-canvas h-full box-border
                    ${isListView ? 'hidden' : 'flex-1 md:w-1/2 opacity-100'}
                    ${isNarrowViewport && isMapView ? 'p-0' : 'p-4'}
                `}>
                    {/* Full-bleed (no padding/border) in mobile/tablet map view; inset + rounded in desktop split. */}
                    <div role="region" aria-label="Map of favourite gyms" className={`h-full w-full overflow-hidden relative ${isNarrowViewport && isMapView ? '' : 'rounded-xl border border-border-default shadow-sm'}`}>
                        <GoogleMapView
                            gyms={gymsForMap.map(gym => ({
                                gymId: gym.id,
                                lat: gym.lat,
                                lng: gym.lng,
                                gymName: gym.name
                            } as MapGym))}
                            selectedGymId={highlightedGymId}
                            onSelectGymId={(gymId) => {
                                const gym = gymsForMap.find(g => g.id === gymId);
                                if (gym) handleMarkerClick(gym);
                                else setHighlightedGymId(null);
                            }}
                            hoveredGymId={hoveredGymId}
                            onHoverChange={setHoveredGymId}
                            isMobile={isMobile}
                            shouldLoadMaps={shouldLoadMaps}
                        />
                    </div>
                </div>
            </div>
        </main>
    );
};

export default FavouritesPage;
